/**
 * Wire schemas for module 11 (messaging): conversations, messages, threads on
 * records, supplier threads, announcements and the unread badge.
 *
 * Synced to the device for project members: `conversation` (read-only) and
 * `message` (append-only events: written offline, sent on reconnect, edited
 * only within 15 minutes of sending). Optional fields default so a newer
 * server never breaks an older client.
 */
import { z } from 'zod';

const text = z.string().default('');
const nullableText = z.string().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const bool = z.boolean().catch(false).default(false);

export const CONVERSATION_KINDS = [
  'channel',
  'direct',
  'record',
  'supplier',
  'announcement',
] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

export const AUDIENCES = [
  'whole_project',
  'contractor_team',
  'owner_side',
  'crew',
  'workers',
] as const;
/** Audiences an announcement may go to (who may post where is the server's call). */
export const ANNOUNCEMENT_AUDIENCES = [
  'whole_project',
  'contractor_team',
  'owner_side',
  'workers',
] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];

/** Kinds of record that carry a discussion (mirrors `messaging.records.RECORD_TYPES`). */
export const RECORD_THREAD_TYPES = [
  'rfi',
  'variation',
  'change_request',
  'selection',
  'certificate',
  'snag',
  'ncr',
  'purchase_order',
  'subcontract',
] as const;
export type RecordThreadType = (typeof RECORD_THREAD_TYPES)[number];

/** Edits are accepted for 15 minutes after the server received the message. */
export const EDIT_WINDOW_MS = 15 * 60 * 1000;

const personRef = z.object({ id: z.string(), name: text });

export const conversationSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  kind: z.string(),
  audience: text,
  title: text,
  party_id: nullableText,
  entity_type: text,
  entity_id: nullableText,
  supplier_org_id: nullableText,
  people: z.array(personRef).catch([]).default([]),
  last_message_at: nullableText,
  message_count: count,
  created_at: nullableText,
  version: z.number().nullable().default(null),
});
export type Conversation = z.infer<typeof conversationSchema>;

const lastMessageSchema = z
  .object({
    author_name: text,
    excerpt: text,
    removed: bool,
    attachment_count: count,
    created_at: nullableText,
  })
  .nullable()
  .catch(null)
  .default(null);

export const conversationRowSchema = conversationSchema.extend({
  display_title: text,
  project_name: text,
  supplier_name: nullableText,
  unread: count,
  muted: bool,
  can_post: bool,
  last_message: lastMessageSchema,
});
export type ConversationRow = z.infer<typeof conversationRowSchema>;

export const participantSchema = z.object({
  id: z.string(),
  name: text,
  organization: text,
  kind: text,
});
export type Participant = z.infer<typeof participantSchema>;

export const attachmentSchema = z.object({
  id: z.string(),
  kind: text,
  status: text,
  content_type: text,
  file_name: text,
  size_bytes: count,
});
export type Attachment = z.infer<typeof attachmentSchema>;

export const messageSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  conversation_id: z.string(),
  author: z
    .object({
      id: nullableText,
      name: text,
      organization: text,
      kind: z.string().default('member'),
    })
    .catch({ id: null, name: '', organization: '', kind: 'member' }),
  body: text,
  mentions: z.array(z.string()).catch([]).default([]),
  reply_to: z
    .object({ id: z.string(), author_name: text, excerpt: text, removed: bool })
    .nullable()
    .catch(null)
    .default(null),
  attachment_count: count,
  attachments: z.array(attachmentSchema).catch([]).default([]),
  created_at: nullableText,
  created_at_device: nullableText,
  edited_at: nullableText,
  removed: bool,
  pinned: bool,
  pinned_at: nullableText,
  seen_by: z.number().nullable().catch(null).default(null),
  recipients: z.number().nullable().catch(null).default(null),
  version: z.number().nullable().default(null),
});
export type Message = z.infer<typeof messageSchema>;

export const conversationDetailSchema = conversationSchema.extend({
  display_title: text,
  can_post: bool,
  can_moderate: bool,
  muted: bool,
  last_read_at: nullableText,
  participants: z.array(participantSchema).catch([]).default([]),
  pinned: z.array(messageSchema).catch([]).default([]),
  recipients: z.number().nullable().catch(null).default(null),
  record_link: nullableText,
});
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

export const announcementSchema = messageSchema.extend({
  audience: text,
  conversation_title: text,
});
export type Announcement = z.infer<typeof announcementSchema>;

export const searchResultSchema = messageSchema.extend({
  conversation_title: text,
  conversation_kind: text,
});
export type SearchResult = z.infer<typeof searchResultSchema>;

export const unreadSchema = z.object({
  total: count,
  projects: z.record(z.string(), z.number()).catch({}).default({}),
  suppliers: z.record(z.string(), z.number()).catch({}).default({}),
});
export type Unread = z.infer<typeof unreadSchema>;

export const personSchema = z.object({
  id: z.string(),
  name: text,
  organization: text,
  party_role: text,
  role: text,
});
export type MessagePerson = z.infer<typeof personSchema>;
